import { useTranslation } from 'react-i18next';
import FormField from '@/components/FormField/FormField';
import TitleText from '@/components/atoms/TitleText';
import InfoText from '@/components/atoms/InfoText';
import ImageUploader from '@/components/atoms/ImageUploader';
import { Controller, useFormContext } from 'react-hook-form';
import { Note } from 'iconsax-react';
import { useState } from 'react';
import { useGetIndustrySpecialitiesQuery, useGetHsCodesQuery } from '../../../../../../store/services/lookups';
import HelperText from '@/components/atoms/HelperText';
import { useAddProductLookups } from '../_context/AddProductLookupsContext';

const ProductInformationForm = () => {
  const { t, i18n } = useTranslation();
  const { control, watch, setValue } = useFormContext();

  const { industries, industriesLoading } = useAddProductLookups();

  const selectedIndustry = watch('categoryId');

  const { data: specialitiesData, isLoading: specialitiesLoading } = useGetIndustrySpecialitiesQuery(
    { categoryId: selectedIndustry },
    { skip: !selectedIndustry }
  );

  const specialities = specialitiesData ?? [];

  const [hsSearch, setHsSearch] = useState('');

  const { data: hsCodesData, isLoading: hsCodesLoading } = useGetHsCodesQuery({ search: hsSearch });

  const hsCodes = hsCodesData ?? [];



  return (
    <div
      id="productInfo"
      className="bg-white dark:bg-dark-surface-primary px-4 md:px-6 pt-5 pb-6 rounded-2xl border border-light-input-border dark:border-dark-input-border shadow-sm mb-8 animate-fade-in"
      data-testid="product-information-form"
    >
      <div
        className="mb-6 flex gap-3 items-center"
        data-testid="product-info-header"
      >
        <div data-testid="product-info-icon-wrapper" className="p-3 bg-soft-background dark:bg-dark-button-bg-secondary rounded-xl text-neutral-900 dark:text-dark-text-primary flex items-center justify-center">
          <Note variant="Bold" size={24} />
        </div>
        <div data-testid="product-info-title-wrapper">
          <TitleText
            text={t('basicInfo')}
            size="lg"
            className="!font-bold !text-[18px] text-[#090909] dark:text-dark-text-primary !leading-[100%] !tracking-[0px]"
          />
          <InfoText
            text={t('basicInfoDesc')}
            size="sm"
            className="!font-normal !text-[12px] text-[#777777] dark:text-dark-text-secondary !leading-[100%] !tracking-[0px] mt-1"
          />
        </div>
      </div>
      <div
        className="flex flex-col gap-6"
        data-testid="product-info-fields-container"
      >
        <FormField
          name="productName"
          id="productName"
          type="text"
          labelClassName="!font-semibold"
          label={t('productName')}
          placeholder={t('productNamePlaceholder')}
          data-testid="product-name-field"
        />

        <FormField
          name="description"
          id="description"
          type="textarea"
          labelClassName="!font-semibold"
          label={t('describeProduct')}
          placeholder={t('describeProductPlaceholder')}
          data-testid="product-description-field"
          rows={5}
          helperText={t('charLimit', { min: 20, max: 1000 })}
          maxLength={1000}
        />
        <div className="flex flex-col md:flex-row gap-6" data-testid="product-info-industry-row">
          <FormField
            name="categoryId"
            id="categoryId"
            type="select"
            options={industries.map((ind) => ({
              label: i18n.exists(ind.code)
                ? t(ind.code as Parameters<typeof t>[0])
                : ind.name,
              value: ind.id,
            }))}
            disabled={industriesLoading}
            loading={industriesLoading}
            onChange={(val) => {
              setValue('categoryId', val, {
                shouldValidate: true,
                shouldDirty: true,
              });
              setValue('specialityId', undefined, {
                shouldValidate: false,
                shouldDirty: true,
              });
            }}
            label={t('industry')}
            labelClassName="!font-semibold"
            placeholder={t('industryPlaceholder')}
            data-testid="industry-field"
          />

          <FormField
            name="specialityId"
            id="specialityId"
            type="select"
            options={specialities.map((spec: { id: string; code?: string; name: string }) => {
              const key = spec.code ?? '';

              return {
                label: key && i18n.exists(key)
                  ? t(key as Parameters<typeof t>[0])
                  : spec.name,
                value: spec.id,
              };
            })}
            disabled={specialitiesLoading || !selectedIndustry}
            loading={specialitiesLoading}
            label={t('specialty')}
            labelClassName="!font-semibold"
            placeholder={t('specialtyPlaceholder')}
            data-testid="speciality-field"
          />
        </div>
        <FormField
          name="hsCodeId"
          id="hsCodeId"
          type="select"
          options={hsCodes.map((item: { id: string; code: string; name: string }) => ({
            label: `${item.code} - ${item.name}`,
            value: item.id,
          }))}
          showSearch
          filterOption={false}
          onSearch={(val: string) => setHsSearch(val)}
          loading={hsCodesLoading}
          label={t('hsCode')}
          placeholder={t('hsCodePlaceholder')}
          labelClassName="!font-semibold"
          data-testid="hs-code-field"
        />

        <Controller
          control={control}
          name="productImages"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <div
              id="productImages"
              className="flex flex-col gap-2 w-full mx-auto"
              data-testid="product-images-container"
            >
              <ImageUploader
                maxFiles={10}
                maxSizeMB={5}
                initialImages={value ?? []}
                onImagesChange={onChange}
              />
              {error && (
                <HelperText
                  status="error"
                  text={error.message || ''}
                  className="mt-1 px-0"
                  data-testid="product-images-error"
                />
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default ProductInformationForm;
